/**
 * The exercise library: a search field, four facets behind one glyph, the selections that are on,
 * and the movements that survived them.
 *
 * ── Why it is here rather than in the rail (owner, 17 September 2026) ────────────────────────
 * „Wyciągnij ją do wspólnego komponentu, filtry i szukajka 1:1." The survey editor's recording
 * question („Które ćwiczenie ma nagrać?") used to open a bare name-filtered list capped at 40 rows,
 * on the reasoning that four facet groups in a 460px dialog would be the rail rebuilt inside a
 * modal. The owner overruled it: a trainer who knows this library from the plan builder should meet
 * the same library here. So the library moved DOWN — out of `features/exercise-library-rail`, into
 * the entity that already owned every decision behind it — and the rail became a shell around it.
 *
 * ── What stayed in the rail, and why ─────────────────────────────────────────────────────────
 * Everything that is about the DAY rather than about the catalogue: the eyebrow naming the session,
 * the title and ✕, the „Pasują do dnia Push A" caption and the affinity behind it, the foot with
 * `Dodaj N ćwiczeń`, the Escape ladder, the hover peek, and the DRAG — whose payload is a plan
 * draft's `ExercisePick`, which an entity may not import from another entity. The rail passes its
 * own chrome in through `heading` and `caption`, and its own `<li>` in through `renderRow`.
 *
 * ── Counts are over the whole catalogue ──────────────────────────────────────────────────────
 * Not over the current results. See `exerciseCatalogue`: a count that shrinks as an unrelated facet
 * narrows cannot be read as "how much of the library is this", and it makes an unticked option look
 * empty when it is not. The count row above the list carries the other figure — how much of the
 * catalogue survived the query — because it stands directly over what it counts.
 *
 * ── One drawn affordance is NOT built, deliberately ──────────────────────────────────────────
 * The 02.08 gallery puts a sort glyph to the right of `Filtry`. Nothing — not the gallery, not the
 * requirements, not this app's model — says what it would sort BY: the list is name-collated and
 * there is no second axis defined anywhere. A control with no defined behaviour is a placeholder, so
 * it is left out and raised rather than invented. The search field takes the width it would have had.
 */

import { Fragment, useId, useState, type KeyboardEvent, type ReactNode } from 'react';

import { EmptyState } from '~/shared/ui/EmptyState';
import { DumbbellMarkIcon } from '~/shared/ui/EmptyStateIcons';
import { FilterIcon } from '~/shared/ui/FilterIcon';

import type { ExerciseLibraryState } from '../lib/useExerciseLibraryQuery';
import type { ExerciseListItem } from '../model/exerciseListItem';
import {
  activeExerciseFilters,
  exerciseFacetGroups,
  type ExerciseFacetLabels,
} from '../model/exerciseFacets';
import type { MuscleGroup } from '../model/muscleGroup';
import type { ExerciseTypeKey } from '../model/exerciseType';

import {
  ExerciseLibraryFilterTabs,
  type ExerciseLibraryFilterLabels,
  type ExerciseLibraryTrainer,
} from './ExerciseLibraryFilterTabs';
import {
  ExerciseLibraryRow,
  type ExerciseLibraryRowProps,
  type ExerciseLibraryVariant,
} from './ExerciseLibraryRow';

import styles from './ExerciseLibraryList.module.css';

/**
 * Every word this surface says, resolved.
 *
 * One object rather than a dozen props for the same reason `ExerciseFacetLabels` is one: `entities/`
 * carries no i18n namespace — a slice below `pages/` cannot know whether its host's copy lives under
 * `web:planBuilder.rail` or `web:reports.editor` — so the host resolves and hands the strings over.
 * Grouping them also makes the omission of one a type error rather than a raw key on screen.
 */
export interface ExerciseLibraryListLabels {
  /** The search field's accessible name, and its placeholder. */
  readonly search: string;
  readonly searchPlaceholder: string;
  /** The filter glyph's name. `filtersWithCount` replaces it once something is set. */
  readonly filters: string;
  readonly filtersWithCount: (count: number) => string;
  /** An active chip's × — the visible label is the filter's own word. */
  readonly removeFilter: (label: string) => string;
  /** „Wyczyść wszystkie", and the name it is read by. */
  readonly clearAll: string;
  readonly clearAllName: (count: number) => string;
  /** „9 ĆWICZEŃ", over the list. */
  readonly count: (count: number) => string;
  /** The list's own name. */
  readonly results: (count: number) => string;
  /**
   * The four states, which are four different sentences because they call for four different next
   * moves. `empty` is „the catalogue holds nothing"; `noMatches` is „the query matched nothing".
   */
  readonly loading: string;
  readonly error: string;
  readonly retry: string;
  readonly empty: string;
  readonly noMatches: string;
  readonly noMatchesHint: string;
  /** Added under `noMatchesHint` when a TERM matched nothing and the other language's names failed. */
  readonly otherLanguageFailed: string;
  /** One row: what a press MEANS here, and the two words under the name. */
  readonly rowAction: (name: string) => string;
  readonly muscle: (group: MuscleGroup) => string;
  readonly type: (typeKey: ExerciseTypeKey) => string;
  /** " · ", between a type and a piece of equipment. Translatable, so it stays a string. */
  readonly metaSeparator: string;
  /** How the facet model words its groups and chips. */
  readonly facets: ExerciseFacetLabels;
  /** What the panel behind the glyph says. */
  readonly panel: ExerciseLibraryFilterLabels;
}

export interface ExerciseLibraryListProps {
  /**
   * The catalogue, the query and the four dispatches — `useExerciseLibraryQuery`'s whole return.
   *
   * ONE prop rather than ten, and the host holds the hook rather than this component calling it: the
   * plan builder's drawer needs the raw `items` for its day-affinity default, and the survey editor
   * needs to disable the read until its dialog is open. Both are answers only a host has.
   */
  readonly library: ExerciseLibraryState;
  /**
   * The rows to draw. Defaults to everything the query left standing.
   *
   * Passed only by the plan builder's drawer, which opens on the movements the day already trains —
   * see `features/exercise-library-rail`'s `dayAffinity`. It is a narrowing of the same list rather
   * than a different one, so the count row and the empty state read it too.
   */
  readonly results?: readonly ExerciseListItem[];
  readonly variant: ExerciseLibraryVariant;
  readonly labels: ExerciseLibraryListLabels;
  /** Who is signed in — the `Źródło` tab marks „Moje" with their avatar. Resolved by the host. */
  readonly trainer: ExerciseLibraryTrainer;
  /**
   * Changes every time the host asks for the library. The search field takes focus on each change,
   * so the affordance that opened the catalogue lands the trainer in the field they were reaching
   * for. `0` — the default — focuses nothing, which is what a surface whose own shell moves focus
   * wants.
   */
  readonly focusSignal?: number;
  /**
   * Which movements are CHOSEN, or `null`/absent where a press acts straight away.
   *
   * One prop rather than a `selectedId` / `selectedIds` pair: two would let a caller name a single
   * choice and a multiple one at once, which is not a state. A single-choice host passes an array of
   * one — or, where it closes on the press and has nothing to mark, nothing at all.
   */
  readonly selectedIds?: readonly number[] | null;
  /** The press. What it means is the host's — choose it, insert it, name it. */
  readonly onPick: (item: ExerciseListItem) => void;
  /**
   * Keys reaching the search field, for a host that has something to do with one.
   *
   * The plan builder's Escape ladder is the whole reason it exists: inside the field Escape means
   * the typed query first and the drawer second, and the field is where focus lands when the library
   * opens. The field's own clearing is NOT here — a host that handles nothing still clears on
   * Escape, because that rung belongs to the field.
   */
  readonly onSearchKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  /** The host's own chrome at the top of the head block — an eyebrow, a title, a ✕. */
  readonly heading?: ReactNode;
  /** The host's own caption under the filters — the drawer's „Pasują do dnia Push A". */
  readonly caption?: ReactNode;
  /**
   * The host's own `<li>`, where it needs one. Given every prop the default row would have had, so
   * a host adds to the row rather than rebuilding it.
   *
   * One host does: the plan builder wraps the row in its own drag gesture and its hover peek. The
   * gesture cannot live here — its payload is a plan draft's `ExercisePick`, and an entity may not
   * import another entity.
   */
  readonly renderRow?: (props: ExerciseLibraryRowProps) => ReactNode;
  /** A drag started somewhere in the list. The drawer takes its hover peek away on it. */
  readonly onDragStart?: () => void;
  /**
   * Offer the filter panel's `Język` tab. OFF by default, and the reason is the HOST: the tab
   * re-keys every content-language read, and a host whose own screen is keyed by it — the builder's
   * plan document, the calendar dock's day editor, the Forma list — would reset under the trainer's
   * hands, unsaved edits and undo included. Only a host with nothing keyed by the language turns it
   * on. The bilingual SEARCH is on everywhere and needs no switch.
   */
  readonly withLanguageTab?: boolean;
}

/** The gallery's magnifier, at the 15px it draws it. */
function SearchIcon() {
  return (
    <svg className={styles.searchIcon} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l4 4" />
    </svg>
  );
}

export function ExerciseLibraryList({
  library,
  results: narrowed,
  variant,
  labels,
  trainer,
  focusSignal = 0,
  selectedIds = null,
  onPick,
  onSearchKeyDown,
  heading,
  caption,
  renderRow,
  onDragStart,
  withLanguageTab = false,
}: ExerciseLibraryListProps) {
  // Closed: the programator mock draws its 320px header as an eyebrow, a title, a search row and a
  // count, and a filter wall standing open there would put the first result below the fold. The
  // 240px column offers no filter control at all.
  const [areFiltersOpen, setFiltersOpen] = useState(false);
  const searchId = useId();
  const filtersId = useId();

  // The 240px column beside the calendar keeps the 02.08 gallery's reduced set — a field and a list.
  // The drawer and the dialog carry the full chrome: the filter glyph, the active selections and the
  // count row over the list.
  const results = narrowed ?? library.catalogue.results;

  // Focus the search whenever the host asks for the library. `key` rather than an effect: remounting
  // the field is what makes `autoFocus` fire again, and it costs nothing — the field's value is the
  // query, which lives in the hook above it.
  const searchKey = `${searchId}-${String(focusSignal)}`;

  const groups = exerciseFacetGroups(library.catalogue, labels.facets);
  const active = activeExerciseFilters(library.catalogue, labels.facets);

  const rowProps = (item: ExerciseListItem): ExerciseLibraryRowProps => ({
    item,
    actionLabel: labels.rowAction(item.name),
    typeLabel: item.typeKey === null ? null : labels.type(item.typeKey),
    muscleLabel:
      item.primaryMuscles[0] === undefined ? null : labels.muscle(item.primaryMuscles[0]),
    equipmentLabel:
      item.equipment.length === 0
        ? null
        : item.equipment.map((piece) => piece.name).join(labels.metaSeparator),
    variant,
    selected: selectedIds === null ? null : selectedIds.includes(item.id),
    onPress: () => {
      onPick(item);
    },
  });

  return (
    <div className={`${String(styles.library)} ${String(styles[variant])}`}>
      <div className={styles.header}>
        {heading}

        <div className={styles.searchRow}>
          <div className={styles.search}>
            <SearchIcon />
            <input
              key={searchKey}
              className={styles.searchInput}
              type="search"
              autoComplete="off"
              aria-label={labels.search}
              placeholder={labels.searchPlaceholder}
              defaultValue={library.query.text}
              autoFocus={focusSignal > 0}
              onChange={(event) => {
                library.setText(event.target.value);
              }}
              /* A query typed and regretted is cleared without leaving the catalogue. The input is
               * uncontrolled — `defaultValue` plus a `key` — so clearing it means writing the
               * element, not a state the render reads. What happens to an Escape on an ALREADY empty
               * field is the host's: in the plan builder's drawer it is the next rung of a ladder,
               * and in a dialog it is Base UI's dismissal. Either way the key is handed on. */
              onKeyDown={(event) => {
                if (event.key === 'Escape' && library.query.text !== '') {
                  event.stopPropagation();
                  event.currentTarget.value = '';
                  library.setText('');
                  return;
                }
                onSearchKeyDown?.(event);
              }}
            />
          </div>
          <button
            type="button"
            className={`${String(styles.filterToggle)} ${String(styles.filterToggleGlyph)}`}
            aria-expanded={areFiltersOpen}
            aria-controls={filtersId}
            /* The mock lights the control on EITHER condition — the panel being out, or a filter
             * being set with the panel away — because both mean "this button is doing something".
             * `aria-expanded` already carries the first; this carries the second, so the
             * stylesheet reads both off the element rather than the component branching on a
             * class name. */
            data-filtered={library.catalogue.activeCount > 0 ? '' : undefined}
            aria-label={
              library.catalogue.activeCount > 0
                ? labels.filtersWithCount(library.catalogue.activeCount)
                : labels.filters
            }
            onClick={() => {
              setFiltersOpen((open) => !open);
            }}
          >
            <FilterIcon className={styles.filterIcon} />
            {/* The mock's control is a GLYPH — 320px has no room for the word beside a search
              * field, so the name is drawn visually hidden and the button reads it. */}
            <span className={styles.filterGlyphName}>{labels.filters}</span>
            {/* The mock badges the glyph with a 7px accent DOT rather than a number: at 34px
              * square there is nowhere for a digit to go. The COUNT is not lost — it rides in the
              * button's own accessible name, so the one channel the dot cannot speak on is the
              * one that carries the actual figure. */}
            {library.catalogue.activeCount > 0 && (
              <i className={styles.filterDot} aria-hidden="true" />
            )}
          </button>
        </div>

        {/* The 07.09 mock's filter tabs. They know nothing about what a filter DOES — they render
          * `exerciseFacetGroups` and dispatch the one `toggleExerciseFacet`. */}
        {areFiltersOpen && (
          <ExerciseLibraryFilterTabs
            id={filtersId}
            groups={groups}
            onToggle={library.toggle}
            onRemove={library.remove}
            onClear={library.clear}
            resultCount={results.length}
            onApply={() => {
              setFiltersOpen(false);
            }}
            trainer={trainer}
            labels={labels.panel}
            withLanguageTab={withLanguageTab}
          />
        )}

        {active.length > 0 && (
          <ul className={styles.activeRow}>
            {active.map((filter) => (
              // The mock puts the TYPE's own colour on its chip and leaves every other facet's
              // plain — a muscle chip in the muscle's hue would put six categorical inks in a row
              // whose job is "here is what you asked for", and equipment has no scale at all. See
              // the filter panel: a category carries no colour on this surface.
              <li key={`${filter.facet}-${filter.id}`}>
                <button
                  type="button"
                  className={styles.activeChip}
                  aria-label={labels.removeFilter(filter.label)}
                  onClick={() => {
                    library.remove(filter.facet, filter.id);
                  }}
                >
                  {filter.label}
                  <i className={styles.activeChipMark} aria-hidden="true">
                    ×
                  </i>
                </button>
              </li>
            ))}
            {/* „Wyczyść wszystkie" appears only once there is more than one chip to clear — beside a
              * single chip it would be a second control for what that chip's own × does. */}
            {active.length > 1 && (
              <li>
                <button
                  type="button"
                  className={styles.clearAll}
                  aria-label={labels.clearAllName(library.catalogue.activeCount)}
                  onClick={library.clear}
                >
                  {labels.clearAll}
                </button>
              </li>
            )}
          </ul>
        )}

        {caption}
      </div>

      {/* The count row — the mock's "9 ĆWICZEŃ". It is the RESULT count rather than the catalogue's,
        * because it stands directly over the list it counts. */}
      {!library.isPending && !library.isError && (
        <p className={styles.countRow}>{labels.count(results.length)}</p>
      )}

      {library.isError ? (
        <p className={styles.notice}>
          {labels.error}
          <button type="button" className={styles.retry} onClick={library.retry}>
            {labels.retry}
          </button>
        </p>
      ) : library.isPending ? (
        <p className={styles.notice}>{labels.loading}</p>
      ) : results.length === 0 ? (
        // A filter combination that matched nothing is an EMPTY STATE, not a sentence in a dashed
        // box — the house pattern, with a drawing and the `neutral` tone: a library with no hits is
        // a window with nothing in it, not a completion and not something to go and fix elsewhere.
        // The one-line notice this used to fall back to was the 240px column's, and that column
        // left with its last host on 19 September 2026.
        <EmptyState
          className={styles.emptyState}
          mark={<DumbbellMarkIcon />}
          tone="neutral"
          title={library.catalogue.total === 0 ? labels.empty : labels.noMatches}
          hint={
            library.catalogue.total === 0
              ? undefined
              : library.query.text.trim() !== '' && library.otherLanguageFailed
                ? `${labels.noMatchesHint} ${labels.otherLanguageFailed}`
                : labels.noMatchesHint
          }
        />
      ) : (
        <ul
          className={styles.results}
          aria-label={labels.results(results.length)}
          /* A drag takes the drawer's peek away, and it is bound HERE rather than in every row:
           * Pragmatic's element adapter drives the native drag, so `dragstart` bubbles to the list.
           * A 236px card standing beside a movement being carried somewhere else is one picture too
           * many, and it would sit under the drag preview. */
          onDragStart={onDragStart}
        >
          {results.map((item) => {
            const props = rowProps(item);
            return (
              <Fragment key={item.id}>
                {renderRow === undefined ? <ExerciseLibraryRow {...props} /> : renderRow(props)}
              </Fragment>
            );
          })}
        </ul>
      )}
    </div>
  );
}
